import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Button,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SmartPromotionModal from './SmartPromotionModal';
import { saveSmartPromotion, reorderSmartPromotions } from '../../api/smartPromotionsApi';

const channelLabels = {
  inApp: 'In-App',
  push: 'Push',
  concierge: 'Concierge',
};

const statusColors = {
  active: 'success',
  paused: 'warning',
  draft: 'default',
};

const STATUS_FILTERS = ['active', 'paused', 'draft'];
const STATUS_BADGE_COLORS = {
  active: 'success.main',
  paused: 'warning.main',
  draft: 'grey.500',
};
const TIME_BUCKET_LABELS = {
  morning: 'Morning · 6:00 – 11:00',
  midday: 'Midday · 11:00 – 16:00',
  earlyEvening: 'Early Evening · 16:00 – 22:00',
  lateNight: 'Late Night · 22:00 – 24:00',
  allDay: 'Always On',
};
const TIME_BUCKET_ORDER = ['morning', 'midday', 'earlyEvening', 'lateNight', 'allDay'];

function readableTargetType(targetType) {
  if (!targetType) return 'Unknown';
  return targetType
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function ChannelChips({ rule }) {
  const chips = [];
  const channels = rule?.channels || {};
  Object.entries(channelLabels).forEach(([key, label]) => {
    const enabled = channels[key] !== false; // default true
    chips.push(
      <Chip
        key={key}
        label={label}
        size="small"
        color={enabled ? 'primary' : 'default'}
        variant={enabled ? 'filled' : 'outlined'}
        sx={{ mr: 1, mb: 1 }}
      />
    );
  });
  return <Box>{chips}</Box>;
}

function TimeBucketChips({ rule }) {
  if (!rule?.timeBuckets || rule.timeBuckets.length === 0) {
    return <Chip label="All day" size="small" />;
  }

  return (
    <Box>
      {rule.timeBuckets.map((bucket) => (
        <Chip key={bucket} label={bucket.replace(/([A-Z])/g, ' $1')} size="small" sx={{ mr: 1, mb: 1 }} />
      ))}
    </Box>
  );
}

function MetaField({ label, children }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          fontWeight: 600,
          color: 'text.secondary',
          mb: 0.75,
          display: 'block',
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function promotionKey(item) {
  return `${item.targetType}-${item.targetId}-${item.parentId || 'root'}`;
}

function SortablePromotionCard({ item, renderCard }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: promotionKey(item),
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
    zIndex: isDragging ? 5 : 1,
    opacity: isDragging ? 0.95 : 1,
  };
  const dragHandleProps = {
    ...listeners,
    ...attributes,
  };
  return (
    <Box ref={setNodeRef} style={style} sx={{ mb: 2 }}>
      {renderCard({ dragProps: dragHandleProps, isDragging })}
    </Box>
  );
}

function PromotionCard({ item, onEdit, dragProps, isDragging = false }) {
  const rule = useMemo(() => item.promotionRules || {}, [item.promotionRules]);
  const status = rule.status || 'draft';
  const statusColor = statusColors[status] || 'default';
  const channels = useMemo(() => <ChannelChips rule={rule} />, [rule]);
  const timeBuckets = useMemo(() => <TimeBucketChips rule={rule} />, [rule]);
  const upsellCopy = rule.upsellCopy;
  const paddingHorizontal = dragProps ? { xs: 3, md: 3.5 } : { xs: 2.5, md: 3 };
  const paddingVertical = dragProps ? { xs: 3.5, md: 3.5 } : { xs: 2.5, md: 3 };
  const draggableAttributes = dragProps
    ? {
        ...dragProps,
        onPointerDown: (event) => {
          dragProps?.onPointerDown?.(event);
        },
        role: 'button',
        'aria-label': 'Drag to reorder promotion',
      }
    : {};

  return (
    <Paper
      elevation={0}
      {...draggableAttributes}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        px: paddingHorizontal,
        py: paddingVertical,
        mb: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#fff',
        boxShadow: isDragging
          ? '0 28px 56px -26px rgba(15, 23, 42, 0.45)'
          : '0 24px 48px -32px rgba(15, 23, 42, 0.28)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        cursor: dragProps ? 'grab' : 'default',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 24px 48px -28px rgba(15, 23, 42, 0.32)',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(235,241,255,0.45) 0%, rgba(255,255,255,0.95) 70%)',
          pointerEvents: 'none',
        }}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="stretch" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            width: { xs: '100%', md: 180 },
            minWidth: { md: 180 },
            borderRadius: 2.5,
            overflow: 'hidden',
            bgcolor: 'grey.100',
            position: 'relative',
          }}
        >
          {item.image ? (
            <Box
              component="img"
              src={item.image}
              alt={item.title}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                width: '100%',
                height: '100%',
                minHeight: 140,
                bgcolor: 'grey.100',
                color: 'grey.500',
                fontSize: 32,
                fontWeight: 600,
              }}
            >
              {item.title?.slice(0, 2)?.toUpperCase() || '–'}
            </Stack>
          )}
          <Chip
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            color={statusColor}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              fontWeight: 600,
              backdropFilter: 'blur(8px)',
            }}
          />
        </Box>

        <Box flex={1} display="flex" flexDirection="column" gap={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {item.title}
              </Typography>
              <Chip label={readableTargetType(item.targetType)} size="small" variant="outlined" />
              {item.parentName && (
                <Chip label={item.parentName} size="small" variant="outlined" sx={{ color: 'text.secondary' }} />
              )}
            </Stack>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => onEdit?.(item)}
              onPointerDown={(event) => event.stopPropagation()}
            >
              Edit Promotion
            </Button>
          </Stack>

          {item.subtitle && (
            <Typography variant="body2" color="text.secondary">
              {item.subtitle}
            </Typography>
          )}
          {upsellCopy && (
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
              {upsellCopy}
            </Typography>
          )}

          <Box
            sx={{
              display: 'grid',
              gap: { xs: 2, md: 3 },
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
            }}
          >
            <MetaField label="Channels">{channels}</MetaField>
            <MetaField label="Time Buckets">{timeBuckets}</MetaField>
            {rule.audienceTags && rule.audienceTags.length > 0 && (
              <MetaField label="Audience">
                {rule.audienceTags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" sx={{ mr: 1, mb: 1 }} />
                ))}
              </MetaField>
            )}
            {rule.blackoutDates && rule.blackoutDates.length > 0 && (
              <MetaField label="Blackout Dates">
                <Stack spacing={0.5}>
                  {rule.blackoutDates.map((range, idx) => (
                    <Typography key={`${range.start}-${range.end}-${idx}`} variant="body2">
                      {new Date(range.start).toLocaleDateString()} – {new Date(range.end).toLocaleDateString()}
                    </Typography>
                  ))}
                </Stack>
              </MetaField>
            )}
            {rule.leadTimeMinutes ? (
              <MetaField label="Lead Time">
                <Chip label={`${rule.leadTimeMinutes} min`} size="small" />
              </MetaField>
            ) : null}
          </Box>

          {rule.notes && (
            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 2,
                borderStyle: 'dashed',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {rule.notes}
              </Typography>
            </Paper>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function SmartPromotionSection({
  entries = [],
  fetchEntries,
  propertyId,
  propertyName,
  error,
  services = [],
  restaurants = [],
  spas = [],
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTERS);
  const [reorderMode, setReorderMode] = useState(false);
  const propertyIdValue = propertyId?.id || propertyId;
  const resolvedPropertyName = propertyName || propertyId?.name || '';
  const hasTargets =
    (services?.length || 0) > 0 ||
    (restaurants?.length || 0) > 0 ||
    (spas?.length || 0) > 0;
  const groupByTimeBuckets = useCallback((list) => {
    const base = TIME_BUCKET_ORDER.reduce((acc, bucket) => {
      acc[bucket] = [];
      return acc;
    }, {});
    (list || []).forEach((item) => {
      const buckets = Array.isArray(item?.promotionRules?.timeBuckets) && item.promotionRules.timeBuckets.length
        ? item.promotionRules.timeBuckets
        : ['allDay'];
      buckets.forEach((bucketRaw) => {
        const bucket = TIME_BUCKET_ORDER.includes(bucketRaw) ? bucketRaw : 'allDay';
        base[bucket].push(item);
      });
    });
    return base;
  }, []);
  const normalizePromotions = useCallback(
    (list) =>
      (list || [])
        .slice()
        .sort(
          (a, b) =>
            (a?.promotionRules?.priority ?? a?.priority ?? 0) - (b?.promotionRules?.priority ?? b?.priority ?? 0)
        ),
    []
  );
  const [orderedPromotions, setOrderedPromotions] = useState(() => normalizePromotions(entries));

  useEffect(() => {
    setOrderedPromotions(normalizePromotions(entries));
  }, [entries, normalizePromotions]);

  useEffect(() => {
    if (reorderMode) {
      setStatusFilter(STATUS_FILTERS);
    }
  }, [reorderMode]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );

  const statusSummary = useMemo(() => {
    const base = STATUS_FILTERS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
    (orderedPromotions || []).forEach((item) => {
      const status = item?.promotionRules?.status || 'draft';
      if (base[status] === undefined) {
        base[status] = 0;
      }
      base[status] += 1;
    });
    return base;
  }, [orderedPromotions]);

  const filteredEntries = useMemo(() => {
    if (!orderedPromotions || orderedPromotions.length === 0) return [];
    return orderedPromotions.filter((item) => {
      const status = item?.promotionRules?.status || 'draft';
      return statusFilter.includes(status);
    });
  }, [orderedPromotions, statusFilter]);

  const handleStatusFilterChange = (_event, value) => {
    if (!value || value.length === 0) {
      return;
    }
    setStatusFilter(value);
  };

  const noPromotions = !orderedPromotions || orderedPromotions.length === 0;
  const noVisiblePromotions = !noPromotions && filteredEntries.length === 0;
  const groupedFilteredPromotions = useMemo(() => groupByTimeBuckets(filteredEntries), [filteredEntries, groupByTimeBuckets]);
  const groupedPrimaryPromotions = useMemo(() => {
    const base = TIME_BUCKET_ORDER.reduce((acc, bucket) => {
      acc[bucket] = [];
      return acc;
    }, {});
    (orderedPromotions || []).forEach((item) => {
      const buckets = Array.isArray(item?.promotionRules?.timeBuckets) && item.promotionRules.timeBuckets.length
        ? item.promotionRules.timeBuckets
        : ['allDay'];
      const primary = TIME_BUCKET_ORDER.find((bucket) => buckets.includes(bucket)) || 'allDay';
      base[primary].push(item);
    });
    return base;
  }, [orderedPromotions]);

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return;
      setOrderedPromotions((prev) => {
        const oldIndex = prev.findIndex((item) => promotionKey(item) === active.id);
        const newIndex = prev.findIndex((item) => promotionKey(item) === over.id);
        if (oldIndex === -1 || newIndex === -1) return prev;
        const reordered = arrayMove(prev, oldIndex, newIndex).map((entry, index) => ({
          ...entry,
          priority: index,
          promotionRules: {
            ...(entry.promotionRules || {}),
            priority: index,
          },
        }));
        const payload = reordered.map((entry, index) => ({
          targetType: entry.targetType,
          targetId: entry.targetId,
          parentId: entry.parentId,
          priority: index,
        }));
        if (propertyIdValue && payload.length) {
          reorderSmartPromotions(propertyIdValue, payload)
            .then(() => fetchEntries?.())
            .catch((err) => {
              console.error('[SmartPromotionSection] Failed to reorder promotions', err);
            });
        }
        return reordered;
      });
    },
    [propertyIdValue, fetchEntries]
  );

  const openCreateModal = () => {
    setEditingPromotion(null);
    setModalOpen(true);
  };

  const openEditModal = (promotion) => {
    setEditingPromotion(promotion);
    setModalOpen(true);
  };

  const handleSavePromotion = async (payload) => {
    if (!propertyId) throw new Error('Missing propertyId');
    await saveSmartPromotion({
      propertyId: propertyId?.id || propertyId,
      targetType: payload.targetType,
      targetId: payload.targetId,
      parentId: payload.parentId,
      promotionRules: payload.promotionRules,
    });
    await fetchEntries();
  };

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(242,244,255,0.92) 0%, rgba(255,255,255,0.97) 65%)',
          boxShadow: '0 24px 48px -36px rgba(47, 99, 209, 0.32)',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>
              Smart Promotions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 620 }}>
              Curate timely offers across dining, spa, and experiences for{' '}
              <strong>{resolvedPropertyName || 'this property'}</strong>. Adjust rules from each service manager or refresh to sync the latest edits.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title={hasTargets ? 'Create a new smart promotion' : 'Add services, menu items, or spa treatments first'}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={openCreateModal}
                  disabled={!hasTargets}
                >
                  New Promotion
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={fetchEntries}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 3, opacity: 0.4 }} />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Visibility
            </Typography>
            <ToggleButtonGroup
              size="small"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              aria-label="Filter promotions by status"
              disabled={reorderMode}
            >
              {STATUS_FILTERS.map((status) => {
                const count = statusSummary[status] ?? 0;
                const selected = statusFilter.includes(status);
                const label = status.charAt(0).toUpperCase() + status.slice(1);
                return (
                  <ToggleButton
                    key={status}
                    value={status}
                    sx={{
                      borderRadius: 999,
                      px: 2,
                      py: 0.75,
                      textTransform: 'none',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        boxShadow: '0 8px 20px -12px rgba(45, 88, 230, 0.6)',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: STATUS_BADGE_COLORS[status] || 'grey.400',
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }}>
                        {label}
                      </Typography>
                      <Chip
                        label={count}
                        size="small"
                        color={selected ? 'secondary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={{ height: 22, fontWeight: 600 }}
                      />
                    </Stack>
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 'auto' }}>
            <Typography variant="caption" color="text.secondary">
              {orderedPromotions?.length || 0} total promotions · {filteredEntries.length} showing
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={reorderMode}
                  onChange={(event) => setReorderMode(event.target.checked)}
                  color="primary"
                />
              }
              label={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <DragIndicatorIcon fontSize="inherit" />
                  <Typography variant="caption" sx={{ fontWeight: reorderMode ? 600 : 500 }}>
                    Reorder mode
                  </Typography>
                </Stack>
              }
              sx={{
                m: 0,
                '& .MuiFormControlLabel-label': {
                  color: reorderMode ? 'text.primary' : 'text.secondary',
                },
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      {reorderMode ? (
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            p: 2,
            borderStyle: 'dashed',
            borderColor: 'primary.light',
            backgroundColor: 'rgba(59,130,246,0.04)',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Drag each card to refine the promotion order. Changes save automatically.
          </Typography>
        </Paper>
      ) : null}

      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'error.light',
            bgcolor: 'error.light',
            color: 'error.contrastText',
          }}
        >
          <Typography variant="body2">
            {error}
          </Typography>
        </Paper>
      )}

      {noPromotions ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px dashed', borderColor: 'divider', textAlign: 'center', bgcolor: '#fff' }}>
          <Typography variant="subtitle1" gutterBottom>
            No promotions configured yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enable smart promotions by editing a service, restaurant item, or spa treatment and toggling the Smart Promotion panel.
          </Typography>
        </Paper>
      ) : reorderMode ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={orderedPromotions.map((item) => promotionKey(item))} strategy={verticalListSortingStrategy}>
            {TIME_BUCKET_ORDER.map((bucket) => {
              const items = groupedPrimaryPromotions[bucket] || [];
              if (!items.length) return null;
              const label = TIME_BUCKET_LABELS[bucket] || bucket;
              return (
                <Box key={bucket} sx={{ mb: 4 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        opacity: 0.4,
                      }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {items.length} {items.length === 1 ? 'promotion' : 'promotions'}
                    </Typography>
                  </Stack>
                  <Stack spacing={2.5}>
                    {items.map((item) => (
                      <SortablePromotionCard
                        key={promotionKey(item)}
                        item={item}
                        renderCard={({ dragProps, isDragging }) => (
                          <PromotionCard
                            item={item}
                            onEdit={openEditModal}
                            dragProps={dragProps}
                            isDragging={isDragging}
                          />
                        )}
                      />
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </SortableContext>
        </DndContext>
      ) : noVisiblePromotions ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px dashed', borderColor: 'divider', textAlign: 'center', bgcolor: '#fff' }}>
          <Typography variant="subtitle1" gutterBottom>
            No promotions match the current filters.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Adjust the status visibility above to reveal additional promotions.
          </Typography>
        </Paper>
      ) : (
        TIME_BUCKET_ORDER.map((bucket) => {
          const items = groupedFilteredPromotions[bucket] || [];
          if (!items.length) return null;
          const label = TIME_BUCKET_LABELS[bucket] || bucket;
          return (
            <Box key={bucket} sx={{ mb: 4 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    opacity: 0.4,
                  }}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {items.length} {items.length === 1 ? 'promotion' : 'promotions'}
                </Typography>
              </Stack>
              <Stack spacing={2.5}>
                {items.map((item) => (
                  <PromotionCard
                    key={`${bucket}-${item.targetType}-${item.targetId}-${item.parentId || 'root'}`}
                    item={item}
                    onEdit={openEditModal}
                  />
                ))}
              </Stack>
            </Box>
          );
        })
      )}

      <SmartPromotionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSavePromotion}
        initialPromotion={editingPromotion}
        services={services}
        restaurants={restaurants}
        spas={spas}
        propertyName={resolvedPropertyName}
      />
    </Box>
  );
}
